// Display preferences that belong to the device rather than to the learner.
//
// Same reasoning as src/app/theme.ts, and the same storage: setupStorage()
// awaits authentication before any setting is readable, db.setSetting reports a
// failed write as "Progress didn't save" — the wrong severity for how a strip is
// drawn — and which rendering you want depends on the screen you are holding.
// `syrinx.theme` and `syrinx.packBaseUrl` set the key namespace.
//
// This module must stay under src/app/ for the same reason theme.ts does:
// importing it from src/core/** would break the node-environment vitest suite by
// touching localStorage.

/**
 * How a recording is drawn on the tappable strip.
 *
 * `waveform` is amplitude over time: loud and quiet, and where the phrases
 * start. `spectrogram` is pitch over time — the axis on which a trill, a buzz
 * and a whistle actually differ, and the one every field guide prints.
 */
export type StripView = 'waveform' | 'spectrogram';

const KEY = 'syrinx.strip';

/**
 * Waveform is the default, and not because it is better.
 *
 * A waveform draws from the 48 peak bytes already inline in region.json: no
 * fetch, no decode, nothing to wait for. A spectrogram needs the recording
 * itself, so switching it on turns every strip you scroll past into a media
 * fetch — instant once the pack is downloaded, and a real download before that.
 * That is a choice to make deliberately, not one to wake up inside.
 */
const DEFAULT: StripView = 'waveform';

/** Pure, and the only branching logic here — the rest touches localStorage. */
export function parseStripView(raw: string | null): StripView {
  return raw === 'spectrogram' || raw === 'waveform' ? raw : DEFAULT;
}

/**
 * Fallback for a device that refuses to store anything (private mode, blocked
 * storage), so the choice at least survives to the next screen.
 */
let inMemory: StripView | null = null;

export function getStripView(): StripView {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw !== null) return parseStripView(raw);
  } catch {
    /* blocked — fall through to whatever this session picked */
  }
  return inMemory ?? DEFAULT;
}

export function setStripView(view: StripView): void {
  inMemory = view;
  try {
    localStorage.setItem(KEY, view);
  } catch {
    // Not fatal: getStripView falls back to the value above.
  }
}

// ---- Haptics ---------------------------------------------------------------

/**
 * Whether answering a question buzzes the device.
 *
 * Device-level like the strip view above, and for a sharper version of the same
 * reason: a phone in a pocket on a bus and a laptop on a desk want opposite
 * answers, and the laptop cannot honour either — `navigator.vibrate` is absent
 * on desktop Safari and on all of iOS, where a web page has no access to the
 * Taptic Engine at all. The setting is therefore an intent, not a promise, and
 * `haptic()` is a no-op wherever the hardware or the browser says no.
 */
const HAPTICS_KEY = 'syrinx.haptics';

/**
 * On by default. The buzz is the fastest channel the app has for "that was
 * wrong" — it lands before the eye has found the colour — and it is the one
 * piece of feedback that survives answering with the phone half-lowered.
 */
const HAPTICS_DEFAULT = true;

let hapticsMemory: boolean | null = null;

export function getHaptics(): boolean {
  try {
    const raw = localStorage.getItem(HAPTICS_KEY);
    if (raw !== null) return raw === 'on';
  } catch {
    /* blocked — fall through to whatever this session picked */
  }
  return hapticsMemory ?? HAPTICS_DEFAULT;
}

export function setHaptics(on: boolean): void {
  hapticsMemory = on;
  try {
    localStorage.setItem(HAPTICS_KEY, on ? 'on' : 'off');
  } catch {
    // Not fatal: getHaptics falls back to the value above.
  }
}

// ---- The side-by-side split ------------------------------------------------

/**
 * Which way the two birds are divided in the compare view.
 *
 * `columns` puts them left and right, which is the arrangement the screen is
 * named for and the one a photograph wants: two pictures at half width are
 * still two pictures. `rows` puts them one above the other, which is what a
 * RECORDING wants — a strip is drawn against time, and the time axis needs
 * every pixel of width it can get, so a spectrogram at half the column is a
 * smear where a full-width one is a shape.
 *
 * Neither is right for both, which is exactly why this is a control rather
 * than a rule: the view shows photographs and recordings at the same time now,
 * so whichever way it splits is wrong for half of what is on screen. The
 * learner turns it to suit whichever half they came to look at.
 *
 * Device-level for the same reason the strip view is: it is a fact about the
 * screen in your hand, not about you. A phone in portrait and a desktop window
 * want opposite answers.
 */
export type CompareLayout = 'columns' | 'rows';

const COMPARE_KEY = 'syrinx.compare.split';

/** Side by side, because that is what "compare" is asked for by name. */
const COMPARE_DEFAULT: CompareLayout = 'columns';

/** Pure, and the only branching logic here — the rest touches localStorage. */
export function parseCompareLayout(raw: string | null): CompareLayout {
  return raw === 'rows' || raw === 'columns' ? raw : COMPARE_DEFAULT;
}

let compareMemory: CompareLayout | null = null;

export function getCompareLayout(): CompareLayout {
  try {
    const raw = localStorage.getItem(COMPARE_KEY);
    if (raw !== null) return parseCompareLayout(raw);
  } catch {
    /* blocked — fall through to whatever this session picked */
  }
  return compareMemory ?? COMPARE_DEFAULT;
}

export function setCompareLayout(layout: CompareLayout): void {
  compareMemory = layout;
  try {
    localStorage.setItem(COMPARE_KEY, layout);
  } catch {
    // Not fatal: getCompareLayout falls back to the value above.
  }
}
